import { shallowReactive, renderSlot as renderSlot$1, createVNode, mergeProps, Fragment, computed, reactive, isVNode, KeepAlive, ref, onMounted, Transition, createTextVNode, inject, provide, toValue, onUnmounted, watch, shallowRef, onBeforeUnmount, toRef, onUpdated, onBeforeMount, h } from "vue";
import { useLink, useRouter } from "vue-router";
function propsWithDefaults(props, defaults) {
  return new Proxy(props, {
    get(target, p, receiver) {
      const value = Reflect.get(target, p, receiver);
      if (value !== void 0) {
        return value;
      }
      return Reflect.get(defaults, p, receiver);
    },
    getOwnPropertyDescriptor(target, p) {
      return Reflect.getOwnPropertyDescriptor(target, p) ?? Reflect.getOwnPropertyDescriptor(defaults, p);
    },
    has(target, p) {
      return Reflect.has(target, p) || Reflect.has(defaults, p);
    },
    ownKeys(target) {
      const keys = /* @__PURE__ */ new Set();
      for (const key of Reflect.ownKeys(target)) {
        keys.add(key);
      }
      for (const key of Reflect.ownKeys(defaults)) {
        keys.add(key);
      }
      return Array.from(keys);
    }
  });
}
const requiredProp = (type, ...alternativeTypes) => ({
  type: alternativeTypes.length > 0 ? [type, ...alternativeTypes] : type,
  required: true
});
const optionalProp = (type, ...alternativeTypes) => ({
  type: alternativeTypes.length > 0 ? [type, ...alternativeTypes] : type,
  required: false,
  default: void 0
});
const anyProp = () => ({
  validator: () => true
});
const componentProp = () => ({
  type: [Object, Function]
});
const refSetter = (ref2) => (value) => ref2.value = value;
function inferProps() {
  throw new Error("inferProps() cannot be called at runtime - did you forget the plugin?");
}
function proxyProp(props, key) {
  if (key !== void 0) {
    return proxyProp(() => props[key]);
  }
  if (typeof props !== "function") {
    throw new Error("Either pass a prop and a key, or a getter function");
  }
  const getProp = props;
  const proxyHandler = {
    get(_, p, receiver) {
      return Reflect.get(getProp(), p, receiver);
    },
    set(_, p, value) {
      const prop = getProp();
      prop[p] = value;
      return true;
    },
    deleteProperty(_, p) {
      return Reflect.deleteProperty(getProp(), p);
    },
    has(_, p) {
      return Reflect.has(getProp(), p);
    },
    ownKeys() {
      return Reflect.ownKeys(getProp());
    },
    getOwnPropertyDescriptor() {
      return {
        enumerable: true,
        configurable: true
      };
    },
    defineProperty(_, property, attributes) {
      return Reflect.defineProperty(getProp(), property, attributes);
    },
    getPrototypeOf() {
      return Reflect.getPrototypeOf(getProp());
    },
    isExtensible() {
      return Reflect.isExtensible(getProp());
    },
    apply(_, thisArg, argArray) {
      return Reflect.apply(getProp(), thisArg, argArray);
    },
    construct(_, argArray, newTarget) {
      return Reflect.construct(getProp(), argArray, newTarget);
    },
    preventExtensions() {
      return Reflect.preventExtensions(getProp());
    },
    setPrototypeOf(_, v) {
      return Reflect.setPrototypeOf(getProp(), v);
    }
  };
  return shallowReactive(new Proxy({}, proxyHandler));
}
function calculateForegroundColor(color) {
  if (color.length !== 7) {
    throw new Error(`Invalid color string ${color}`);
  }
  color = color.substring(1);
  const colorNumber = Number.parseInt(color, 16);
  const [r, g, b] = [colorNumber >> 16 & 255, colorNumber >> 8 & 255, colorNumber & 255].map((value) => value / 255).map((value) => value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4));
  const luminance = r * 0.2162 + g * 0.7152 + b * 0.0722;
  return luminance < Math.sqrt(1.05 * 0.05) - 0.05 ? "#ffffff" : "#000000";
}
function castUnsafe(value) {
  return value;
}
function clamp(value, min, max) {
  const l = Math.min(min, max);
  const u = Math.max(min, max);
  return Math.max(l, Math.min(value, u));
}
function debounce(action, timeout = 200) {
  let timer;
  return (...args) => {
    if (timer !== null) {
      window.clearTimeout(timer);
    }
    timer = window.setTimeout(() => action(...args), timeout);
  };
}
class Deferred {
  resolveFn;
  rejectFn;
  promise = new Promise((resolve, reject) => {
    this.resolveFn = resolve;
    this.rejectFn = reject;
  });
  resolve(value) {
    this.resolveFn(value);
  }
  reject(error) {
    this.rejectFn(error);
  }
  then(onfulfilled, onrejected) {
    return this.promise.then(onfulfilled, onrejected);
  }
  catch(onrejected) {
    return this.promise.catch(onrejected);
  }
  finally(onfinally) {
    return this.promise.finally(onfinally);
  }
  bind(promise) {
    void promise.then((value) => this.resolve(value));
    void promise.catch((e) => this.reject(e));
  }
  get [Symbol.toStringTag]() {
    return this.promise[Symbol.toStringTag];
  }
}
function defineGlobal(key, value) {
  const globals = window;
  globals[key] = value;
}
function defineGlobals(toDefine) {
  return Object.entries(toDefine).forEach(([key, value]) => defineGlobal(key, value));
}
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function waitForDocumentReady() {
  if (document.readyState === "loading") {
    await new Promise((resolve) => document.addEventListener("DOMContentLoaded", resolve));
  }
}
const downloadFile = async (data, fileName) => {
  if ("blob" in data) {
    data = await data.blob();
  }
  const dataUrl = window.URL.createObjectURL(data);
  try {
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = dataUrl;
    a.download = fileName;
    a.click();
  } finally {
    window.URL.revokeObjectURL(dataUrl);
  }
};
function findFileNameFromHeaders(headers) {
  const headerValue = headers.get("Content-Disposition");
  return headerValue && findFileNameFromHeader(headerValue);
}
function findFileNameFromHeader(headerValue) {
  for (const part of headerValue.split(";").map((v) => v.trim())) {
    const index = part.indexOf("=");
    if (index < 0) {
      continue;
    }
    const left = part.substring(0, index).trim();
    const right = part.substring(index + 1).trim();
    if (left === "filename") {
      if (right.startsWith('"') && right.endsWith('"')) {
        return JSON.parse(right);
      }
      return right;
    }
  }
  return null;
}
function formatFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1e3 : 1024;
  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }
  const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
  return `${bytes.toFixed(dp)} ${units[u]}`;
}
const getUnique = (items, getKey) => {
  const set = /* @__PURE__ */ new Set();
  const result = [];
  for (const item of items) {
    const key = getKey ? getKey(item) : item;
    if (set.has(key)) continue;
    set.add(key);
    result.push(item);
  }
  return result;
};
const groupBy = (items, selector) => {
  const result = {};
  for (const item of items) {
    let key;
    if (typeof selector === "string") {
      key = item[selector];
    } else if (typeof selector === "function") {
      key = selector(item);
    } else {
      throw new Error(`Unknown selector type ${typeof selector}`);
    }
    let arr = result[key];
    if (!arr) {
      arr = [];
      result[key] = arr;
    }
    arr.push(item);
  }
  return result;
};
function hsvToRGB(h2, s, v) {
  let r = 0, g = 0, b = 0;
  const i = Math.floor(h2 * 6);
  const f = h2 * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
function lazy(getValue) {
  let value;
  let initialised = false;
  return () => {
    if (initialised) {
      return value;
    }
    value = getValue();
    initialised = true;
    return value;
  };
}
function mapDefined(input, mapping) {
  const result = [];
  for (const item of input) {
    const mapped = mapping(item);
    if (mapped !== void 0) {
      result.push(mapped);
    }
  }
  return result;
}
function mapValues(input, mapping) {
  const result = {};
  for (const key in input) {
    result[key] = mapping(input[key], key);
  }
  return result;
}
async function mergePromises(entries) {
  const result = {};
  for (const key in entries) {
    result[key] = await entries[key];
  }
  return result;
}
async function runWithMinimumDelay(action, minimumTimeMs) {
  const t1 = Date.now();
  const result = typeof action === "function" ? await action() : await action;
  const t2 = Date.now();
  if (t2 - t1 <= minimumTimeMs) {
    await delay(minimumTimeMs - (t2 - t1));
  }
  return result;
}
function paged(page, pageSize, items) {
  const totalPages = getPageCount(pageSize, items.length);
  if (page < 1 || page > totalPages) {
    return [];
  }
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, items.length);
  return items.slice(startIndex, endIndex);
}
function getPageCount(pageSize, totalItems) {
  return Math.ceil(totalItems / pageSize);
}
function positiveMod(value, mod) {
  return (value % mod + mod) % mod;
}
function isSorted(items, comparator) {
  if (items.length === 0) {
    return true;
  }
  let previous = items[0];
  for (let i = 1; i < items.length; i++) {
    const item = items[i];
    if (comparator(previous, item) > 0) {
      return false;
    }
    previous = item;
  }
  return true;
}
function sortWithDirection(items, direction, comparator) {
  const realComparator = direction === "ascending" ? comparator : (a, b) => comparator(a, b) * -1;
  items.sort(realComparator);
}
function stylesheet(template, ...expr) {
  return String.raw(template, ...expr);
}
function serialiseQueryValue(value) {
  if (value === null) {
    return "null";
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  switch (typeof value) {
    case "string":
      return value;
    case "boolean":
      return value;
    case "number":
      return value;
    case "bigint":
      return value.toString();
    case "symbol":
      return value.toString();
  }
  return JSON.stringify(value);
}
function sanitiseQueryParams(params) {
  return Object.entries(params).filter(([, value]) => value !== void 0 && value !== null).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map((item) => `${key}=${encodeURIComponent(serialiseQueryValue(item))}`);
    } else {
      return [`${key}=${encodeURIComponent(serialiseQueryValue(value))}`];
    }
  }).join("&");
}
function urlWithParams(url, params) {
  const queryParams = sanitiseQueryParams(params);
  if (queryParams.length > 0) {
    return `${url}?${queryParams}`;
  }
  return url;
}
const defineComponent = (component, props, options) => {
  if (typeof component === "function") {
    const config = {
      name: component.name,
      props: props ?? {},
      setup: component,
      inheritAttrs: false,
      ...options
    };
    return defineComponent(config);
  }
  return {
    ...component,
    props: fixProps(component.props ?? [])
  };
};
function fixProps(props) {
  if (Array.isArray(props)) {
    return props;
  }
  const propsAs = props;
  return mapValues(propsAs, fixProp);
}
function fixProp(prop) {
  if (Array.isArray(prop) || typeof prop === "function" || !(typeof prop === "object")) {
    return fixProp({
      type: prop
    });
  }
  if (prop.required || "default" in prop) {
    return prop;
  }
  return {
    ...prop,
    required: false,
    default: void 0
  };
}
function renderSlot(slots, name = "default") {
  return renderSlot$1(slots, name);
}
const ModalTitle = /* @__PURE__ */ defineComponent({
  name: "Modal",
  props: {
    title: requiredProp(String)
  },
  setup(props, {
    attrs
  }) {
    return () => createVNode("h3", mergeProps({
      "class": "vu-modal-title"
    }, attrs), [props.title]);
  }
});
const Alert = /* @__PURE__ */ defineComponent({
  name: "Alert",
  props: {
    title: requiredProp(String)
  },
  setup(props, {
    slots
  }) {
    return () => createVNode(Fragment, null, [createVNode(ModalTitle, {
      "title": props.title
    }, null), createVNode("hr", null, null), createVNode("div", null, [renderSlot$1(slots, "default")])]);
  }
});
const AutoColumns = /* @__PURE__ */ defineComponent({
  name: "AutoColumns",
  props: {
    size: requiredProp(String),
    autoFit: optionalProp(Boolean)
  },
  setup(props, {
    attrs,
    slots
  }) {
    const fillMode = computed(() => props.autoFit ? "auto-fit" : "auto-fill");
    return () => createVNode("div", mergeProps({
      "class": "vu-auto-columns",
      "style": {
        "--fill-mode": fillMode.value,
        "--size": props.size
      }
    }, attrs), [renderSlot$1(slots, "default")]);
  }
});
const ColumnLayout = /* @__PURE__ */ defineComponent({
  name: "ColumnLayout",
  props: {
    spacing: optionalProp(String),
    columns: requiredProp(Number)
  },
  setup(props, {
    attrs,
    slots
  }) {
    return () => createVNode("div", mergeProps({
      "style": {
        display: "grid",
        gridTemplateColumns: `repeat(${props.columns}, 1fr)`,
        gap: props.spacing ?? "2rem"
      }
    }, attrs), [renderSlot$1(slots, "default")]);
  }
});
const Link = /* @__PURE__ */ defineComponent({
  name: "Link",
  props: {
    href: requiredProp(String),
    replace: optionalProp(Boolean)
  },
  setup(props, {
    slots,
    attrs
  }) {
    const link = reactive(useLink({
      get to() {
        return props.href;
      },
      get replace() {
        return props.replace;
      }
    }));
    return () => createVNode("a", mergeProps(attrs, {
      "href": link.href,
      "onClick": (e) => void link.navigate(e)
    }), [renderSlot$1(slots, "default")]);
  }
});
function _isSlot(s) {
  return typeof s === "function" || Object.prototype.toString.call(s) === "[object Object]" && !isVNode(s);
}
const FormTabControls = /* @__PURE__ */ defineComponent({
  name: "FormTabControls",
  props: {
    tabs: requiredProp(Array),
    selectedTabId: requiredProp(String),
    setSelectedTab: requiredProp(Function)
  },
  setup(props, {
    attrs
  }) {
    function renderTabInner(tab) {
      return createVNode("div", null, [createVNode("span", null, [tab.name]), createVNode("div", null, null)]);
    }
    function renderTabContent(tab) {
      let _slot;
      if (tab.url) {
        const type = tab.linkType ?? "router";
        switch (type) {
          case "href":
            return createVNode("a", {
              "href": tab.url
            }, [renderTabInner(tab)]);
          case "router":
            return createVNode(Link, {
              "href": tab.url
            }, _isSlot(_slot = renderTabInner(tab)) ? _slot : {
              default: () => [_slot]
            });
        }
      }
      return renderTabInner(tab);
    }
    function renderTab(tab) {
      if (tab.renderTab) {
        return createVNode(tab.renderTab, {
          "active": props.selectedTabId === tab.id,
          "onClick": () => props.setSelectedTab(tab.id)
        }, null);
      }
      if (tab.url) {
        return createVNode("li", {
          "key": tab.id
        }, [renderTabContent(tab)]);
      }
      return createVNode("li", {
        "key": tab.id,
        "data-active": props.selectedTabId === tab.id,
        "onClick": () => props.setSelectedTab(tab.id)
      }, [renderTabContent(tab)]);
    }
    return () => createVNode("ul", mergeProps({
      "class": "vu-form-tab-controls"
    }, attrs), [props.tabs.map(renderTab)]);
  }
});
let FormTabsRenderingMethod = /* @__PURE__ */ function(FormTabsRenderingMethod2) {
  FormTabsRenderingMethod2["KeepAlive"] = "keepAlive";
  FormTabsRenderingMethod2["DisplayNone"] = "displayNone";
  FormTabsRenderingMethod2["RenderActive"] = "renderActive";
  return FormTabsRenderingMethod2;
}({});
const FormTabs = /* @__PURE__ */ defineComponent({
  name: "FormTabs",
  props: {
    tabs: requiredProp(Array),
    selectedTabId: requiredProp(String),
    setSelectedTab: requiredProp(Function),
    renderingMethod: optionalProp(String),
    controlAttributes: optionalProp(Object)
  },
  setup(props, {
    slots
  }) {
    const selectedTab = computed(() => props.tabs.find((tab) => tab.id === props.selectedTabId));
    function renderTab(tab) {
      const TabContent = tab.renderContent;
      return TabContent ? createVNode(TabContent, {
        "key": tab.id
      }, null) : renderSlot$1(slots, tab.id);
    }
    function renderContent() {
      switch (props.renderingMethod ?? FormTabsRenderingMethod.DisplayNone) {
        case FormTabsRenderingMethod.DisplayNone:
          return props.tabs.map((tab) => createVNode("div", {
            "key": tab.id,
            "style": {
              display: props.selectedTabId === tab.id ? "contents" : "none"
            }
          }, [renderTab(tab)]));
        case FormTabsRenderingMethod.KeepAlive: {
          return createVNode(KeepAlive, null, [selectedTab.value ? renderTab(selectedTab.value) : null]);
        }
        case FormTabsRenderingMethod.RenderActive:
          return selectedTab.value ? renderTab(selectedTab.value) : null;
      }
    }
    return () => createVNode(Fragment, null, [createVNode(FormTabControls, mergeProps({
      "selectedTabId": props.selectedTabId,
      "setSelectedTab": props.setSelectedTab,
      "tabs": props.tabs
    }, props.controlAttributes), null), renderContent()]);
  }
});
const Modal = /* @__PURE__ */ defineComponent({
  name: "Modal",
  props: {
    zIndex: requiredProp(Number),
    backgroundClick: optionalProp(Function),
    onCancel: optionalProp(Function)
  },
  setup(props, {
    slots,
    attrs
  }) {
    const modalRef = ref();
    onMounted(async () => {
      modalRef.value?.addEventListener("cancel", (e) => props.onCancel?.(e));
      modalRef.value?.showModal();
    });
    function handleClick(e) {
      const element = e.target;
      if (element instanceof HTMLDialogElement) {
        const rect = element.getBoundingClientRect();
        if (e.clientX < rect.left || e.clientY < rect.top || e.clientX > rect.x + rect.width || e.clientY > rect.y + rect.height) {
          props.backgroundClick?.(e);
        }
      }
    }
    return () => createVNode(Transition, {
      "name": "vu-modal-fade"
    }, {
      default: () => [createVNode("dialog", mergeProps({
        "ref": modalRef,
        "class": "vu-modal",
        "onClick": handleClick,
        "style": {
          zIndex: props.zIndex
        }
      }, attrs), [renderSlot$1(slots, "default")])]
    });
  }
});
const Required = /* @__PURE__ */ defineComponent({
  name: "Required",
  props: {
    label: requiredProp(String)
  },
  setup(props, {
    attrs
  }) {
    return () => createVNode("div", mergeProps({
      "class": "vu-required"
    }, attrs), [props.label, createVNode("div", null, [createTextVNode("*")])]);
  }
});
const ScrollPane = /* @__PURE__ */ defineComponent({
  name: "ScrollPane",
  setup(_, {
    slots,
    attrs
  }) {
    return () => createVNode("div", mergeProps({
      "class": "vu-scroll-pane"
    }, attrs), [createVNode("div", null, [renderSlot$1(slots, "default")])]);
  }
});
const ShadowContainer = /* @__PURE__ */ defineComponent({
  name: "ShadowContainer",
  setup(_, {
    attrs,
    slots
  }) {
    return () => createVNode("div", mergeProps({
      "class": "vu-shadow-container"
    }, attrs), [renderSlot$1(slots, "default")]);
  }
});
const ToggleButton = /* @__PURE__ */ defineComponent({
  name: "ToggleButton",
  props: {
    toggled: requiredProp(Boolean),
    setToggled: requiredProp(Function),
    disabled: optionalProp(Boolean)
  },
  setup(props, {
    attrs
  }) {
    return () => createVNode("button", mergeProps({
      "disabled": props.disabled,
      "class": "vu-toggle-button",
      "type": "button",
      "data-toggled": props.toggled ? "true" : void 0,
      "onClick": () => props.setToggled(!props.toggled)
    }, attrs), [createVNode("span", null, null)]);
  }
});
function useService(key) {
  const handler = inject(key);
  if (!handler) {
    throw new Error(`${key.description ?? "Service"} has not been registered`);
  }
  return handler;
}
const ValidationHandlerKey = Symbol("ValidationHandler");
function useValidationHandler() {
  return useService(ValidationHandlerKey);
}
function useCustomFormValidation(options) {
  const handler = new ValidationHandler(options);
  provide(ValidationHandlerKey, handler);
  return handler;
}
class ValidationHandler {
  inputValidations = /* @__PURE__ */ new Map();
  validations = /* @__PURE__ */ new Map();
  customChecks = /* @__PURE__ */ new Map();
  constructor(options) {
    this.options = options;
  }
  addInputValidation(validation) {
    const symbol = Symbol();
    this.inputValidations.set(symbol, validation);
    return symbol;
  }
  async runInputValidations() {
    for (const validation of this.inputValidations.values()) {
      await validation(this);
    }
  }
  removeInputValidation(key) {
    return this.inputValidations.delete(key);
  }
  addCustomValidation(validation) {
    const symbol = Symbol();
    this.validations.set(symbol, validation);
    return symbol;
  }
  async runCustomValidations() {
    const results = [];
    for (const check of this.validations.values()) {
      const result = await check(this);
      results.push({
        validation: check,
        result,
        getErrors() {
          if (typeof this.result === "string") {
            return [this.result];
          }
          if (Array.isArray(this.result)) {
            return this.result;
          }
          return [];
        },
        isSuccess() {
          return !(this.result === false || typeof this.result === "string" && this.result.trim().length > 0 || Array.isArray(this.result) && this.result.length > 0);
        }
      });
    }
    return results;
  }
  removeCustomValidation(key) {
    return this.validations.delete(key);
  }
  addCustomCheck(check) {
    const symbol = Symbol();
    this.customChecks.set(symbol, check);
    return symbol;
  }
  /**
   * @returns The failing check, or null if all passed
   */
  async runCustomChecks() {
    for (const check of this.customChecks.values()) {
      if (!await check(this)) {
        return check;
      }
    }
    return null;
  }
  removeCustomCheck(key) {
    return this.customChecks.delete(key);
  }
  async validateForm() {
    const form = toValue(this.options.form);
    if (form) {
      await this.runInputValidations();
      if (!form.reportValidity()) {
        return {
          formValid: false,
          valid: false,
          successful: false
        };
      }
    }
    const validationResults = await this.runCustomValidations();
    const failedCustomValidations = validationResults.filter((validation) => !validation.isSuccess());
    const errorMessages = validationResults.flatMap((validation) => validation.getErrors());
    if (failedCustomValidations.length > 0) {
      return {
        errorMessages,
        failedCustomValidations,
        formValid: true,
        valid: false,
        successful: false
      };
    }
    const failingCheck = await this.runCustomChecks();
    return {
      failedCheck: failingCheck,
      formValid: true,
      valid: true,
      successful: failingCheck === null
    };
  }
}
function useCustomCheck(check) {
  const handler = useValidationHandler();
  let symbol;
  onMounted(() => symbol = handler.addCustomCheck(check));
  onUnmounted(() => handler.removeCustomCheck(symbol));
}
function useCustomValidation(validation) {
  const handler = useValidationHandler();
  let symbol;
  onMounted(() => symbol = handler.addCustomValidation(validation));
  onUnmounted(() => handler.removeCustomValidation(symbol));
}
function useInputValidation(elementRef, validate, clearOnChange = true) {
  async function checkValidity() {
    const elementValue = toValue(elementRef);
    if (elementValue) {
      const rawResult = await validate(elementValue);
      const errorMessage = rawResult === true ? "" : rawResult.trim();
      elementValue.setCustomValidity(errorMessage);
    }
  }
  if (clearOnChange) {
    let updateListener = function(newEl, oldEl) {
      for (const name of ["input", "change"]) {
        oldEl?.removeEventListener(name, changeListener);
        newEl?.addEventListener(name, changeListener);
      }
    };
    const changeListener = (e) => {
      const target = e.target;
      const el = elementRef.value;
      if (!!target && target === el) {
        el.setCustomValidity("");
      }
    };
    watch(elementRef, updateListener, { immediate: true });
  }
  return useCustomInputValidation(checkValidity);
}
function useCustomInputValidation(validation) {
  const handler = useValidationHandler();
  let symbol;
  onMounted(() => symbol = handler.addInputValidation(validation));
  onUnmounted(() => handler.removeInputValidation(symbol));
}
const useInterval = (interval, action) => {
  const intervalId = ref(null);
  onMounted(() => {
    intervalId.value = window.setInterval(action, interval);
  });
  onUnmounted(() => {
    if (intervalId.value !== null) {
      window.clearInterval(intervalId.value);
    }
  });
};
const usePollRef = (pollRateMillis, getValue) => {
  let hasValue = false;
  const value = shallowRef();
  const updateValue = () => {
    value.value = getValue();
    hasValue = true;
  };
  useInterval(pollRateMillis, updateValue);
  return computed(() => {
    if (!hasValue) {
      updateValue();
    }
    return value.value;
  });
};
function useMouse() {
  const x = ref(0);
  const y = ref(0);
  const updatePos = (e) => {
    x.value = e.clientX;
    y.value = e.clientY;
  };
  onMounted(() => {
    window.addEventListener("mousemove", updatePos);
    window.addEventListener("mouseup", updatePos);
    window.addEventListener("mousedown", updatePos);
    window.addEventListener("mouseenter", updatePos);
    window.addEventListener("mouseleave", updatePos);
    window.addEventListener("mouseover", updatePos);
  });
  onUnmounted(() => {
    window.removeEventListener("mousemove", updatePos);
    window.removeEventListener("mouseup", updatePos);
    window.removeEventListener("mousedown", updatePos);
    window.removeEventListener("mouseenter", updatePos);
    window.removeEventListener("mouseleave", updatePos);
    window.removeEventListener("mouseover", updatePos);
  });
  return {
    x,
    y
  };
}
const useOnInput = (action) => (e) => {
  action.bind(e)(e.target.value, e);
};
const useOnInputNumber = (action) => (e) => {
  const element = e.target;
  if (element.valueAsNumber !== void 0) {
    action.bind(e)(element.valueAsNumber, e);
    return;
  }
  const value = Number.parseFloat(element.value);
  action.bind(e)(value, e);
};
const useOnInputDate = (action) => (e) => {
  const element = e.target;
  if (element.valueAsDate !== void 0) {
    action.bind(e)(element.valueAsDate, e);
    return;
  }
  const date = new Date(element.value);
  return Number.isNaN(date.valueOf()) ? null : date;
};
function useOptionalRouterParam(options) {
  const router = useRouter();
  const { paramName, paramUrl, baseUrl, options: queryOptions, mode = "replace" } = options;
  function formatUrl(value) {
    if (value === null) {
      return baseUrl;
    }
    const stringified = queryOptions.stringify(value);
    if (typeof stringified !== "string") {
      throw new Error("Route param must serialize to a string");
    }
    return paramUrl.replaceAll("%param%", stringified);
  }
  return computed({
    get() {
      const params = router.currentRoute.value.params;
      if (!(paramName in params)) {
        return null;
      }
      return queryOptions.parse(params[paramName]);
    },
    set(newValue) {
      const newUrl = formatUrl(newValue);
      switch (mode) {
        case "push":
          void router.push(newUrl);
          break;
        case "replace":
          void router.replace(newUrl);
          break;
        default:
          throw new Error("Invalid mode");
      }
    }
  });
}
function parsingString(parse) {
  return (query) => {
    if (query === null) {
      throw new Error("Expected value, got null");
    } else if (query instanceof Array) {
      throw new Error("Expected value, got array of values");
    }
    return parse(query);
  };
}
function parsingNullableString(parse) {
  return (query) => {
    if (query === null || query === "") {
      return null;
    } else if (query instanceof Array) {
      throw new Error("Expected value, got array of values");
    }
    return parse(query);
  };
}
function asNullableQuery(query) {
  return {
    stringify(item) {
      if (item === null) {
        return "";
      }
      return query.stringify(item);
    },
    parse(queryValue) {
      if (queryValue === null || queryValue === "") {
        return null;
      }
      return query.parse(queryValue);
    }
  };
}
function createArrayQuery(query) {
  return {
    stringify: (items) => {
      return items.map((item) => {
        const stringified = query.stringify(item);
        if (Array.isArray(stringified)) {
          throw new Error("Cannot return an array in an array query");
        }
        return stringified;
      });
    },
    parse(queryValue) {
      if (queryValue === null) {
        return [];
      }
      if (Array.isArray(queryValue)) {
        return queryValue.map(query.parse);
      }
      return [query.parse(queryValue)];
    }
  };
}
function createEnumQuery(enumValues) {
  const options = enumValues instanceof Array ? enumValues : Object.values(enumValues);
  return {
    parse: parsingString((str) => {
      if (options.includes(str)) {
        return str;
      }
      for (const option of options) {
        if (str.localeCompare(option) === 0) {
          return option;
        }
      }
      throw new Error(`Unknown option ${str}`);
    }),
    stringify: (item) => item
  };
}
function createNullableEnumQuery(enumValues) {
  return asNullableQuery(createEnumQuery(enumValues));
}
const StringQuery = {
  parse: parsingString((str) => str),
  stringify: (str) => str
};
const IntQuery = {
  parse: parsingString((str) => Number.parseInt(str)),
  stringify: (x) => x.toString()
};
const NullableIntQuery = asNullableQuery(IntQuery);
function createFloatQuery(precision) {
  return {
    parse: parsingString(Number.parseFloat),
    stringify: (value) => value.toFixed(precision)
  };
}
function createNullableFloatQuery(precision) {
  return asNullableQuery(createFloatQuery(precision));
}
const BooleanQuery = {
  parse: parsingString((str) => str === "true" ? true : false),
  stringify: (x) => x.toString()
};
const NullableBooleanQuery = asNullableQuery(BooleanQuery);
const queriesToUpdate = /* @__PURE__ */ new Map();
let updatingRoutes = false;
async function runRouteUpdate(router) {
  while (queriesToUpdate.size > 0) {
    const newParams = {};
    for (const key of queriesToUpdate.keys()) {
      const record = queriesToUpdate.get(key);
      newParams[record.key] = record.value;
    }
    queriesToUpdate.clear();
    try {
      await updateRouter(router, newParams);
    } catch (e) {
      console.error("Failed to update URL query", e);
    } finally {
      updatingRoutes = false;
    }
  }
  updatingRoutes = false;
}
async function updateRouter(router, entries) {
  const route = router.currentRoute.value;
  const newQuery = {
    ...route.query,
    ...entries
  };
  await router.replace({
    ...route,
    query: newQuery
  });
}
function addRouteQueryUpdate(identifier, name, value, router) {
  queriesToUpdate.set(identifier, {
    key: name,
    value
  });
  if (!updatingRoutes) {
    updatingRoutes = true;
    queueMicrotask(() => void runRouteUpdate(router));
  }
}
function removeRouteQueryUpdate(identifier) {
  const existing = queriesToUpdate.get(identifier);
  if (!queriesToUpdate.delete(identifier)) {
    return void 0;
  }
  return existing;
}
const serializeQueryParam = (value) => JSON.stringify(value ?? null);
function useQueryParam(name, options, ref2) {
  const router = useRouter();
  const uniqueReference = Symbol("useQuery:" + name);
  let unmounted = false;
  let currentParamValue = serializeQueryParam(router.currentRoute.value.query[name]);
  function updateNewValue(value) {
    if (unmounted) {
      return;
    }
    const newValue = options.stringify(value);
    const serialized = serializeQueryParam(newValue);
    if (serialized !== currentParamValue) {
      currentParamValue = serialized;
      addRouteQueryUpdate(uniqueReference, name, newValue, router);
    }
  }
  function updateValueFromRoute(query = router.currentRoute.value.query) {
    if (unmounted) {
      return;
    }
    if (!(name in query)) {
      updateNewValue(ref2.value);
      return;
    }
    try {
      ref2.value = options.parse(query[name]);
    } catch (e) {
      console.error(e);
    }
  }
  watch(router.currentRoute, (route) => {
    if (unmounted) {
      return;
    }
    const newValue = serializeQueryParam(route.query[name]);
    if (currentParamValue !== newValue) {
      currentParamValue = newValue;
      updateValueFromRoute(route.query);
    }
  });
  onBeforeUnmount(() => {
    unmounted = true;
    removeRouteQueryUpdate(uniqueReference);
  });
  updateValueFromRoute();
  watch(ref2, updateNewValue);
}
function useQueryRef(name, defaultValue, options) {
  const value = shallowRef(defaultValue);
  useQueryParam(name, options, value);
  return value;
}
function useQueryStoreRef(name, store, key, options) {
  const ref2 = toRef(store, key);
  useQueryParam(name, options, ref2);
  return ref2;
}
function useComputedQueryRef(name, options, defaultValue) {
  const router = useRouter();
  return computed(() => {
    const query = router.currentRoute.value.query;
    if (name in query) return options.parse(query[name]);
    if (arguments.length >= 3) return defaultValue;
    return options.parse(null);
  });
}
const RefQueries = {
  Boolean: BooleanQuery,
  NullableBoolean: NullableBooleanQuery,
  Int: IntQuery,
  NullableInt: NullableIntQuery,
  createFloatQuery,
  createNullableFloatQuery,
  String: StringQuery,
  enum: createEnumQuery,
  nullableEnum: createNullableEnumQuery,
  asNullable: asNullableQuery,
  arrayOf: createArrayQuery
};
const useReordering = (options) => {
  const positions = reactive(/* @__PURE__ */ new Map());
  const items = shallowRef([...toValue(options.items)]);
  watch(
    () => toValue(options.items),
    (newItems) => {
      items.value = [...newItems];
    }
  );
  const { x: mouseX, y: mouseY } = useMouse();
  const isReordering = ref(false);
  function cancel() {
    for (const pos of positions.values()) {
      pos.transform = 0;
    }
    isReordering.value = false;
  }
  function selectMouseCoord() {
    if (options.direction === "vertical") {
      return mouseY.value;
    }
    return mouseX.value;
  }
  function selectSize(position) {
    if (options.direction === "vertical") {
      return position.height;
    }
    return position.width;
  }
  function updateSizing(itemId, width, height) {
    const position = positions.get(itemId);
    if (position) {
      position.width = width;
      position.height = height;
    }
  }
  function confirm(e) {
    let position = 0;
    const itemPositions = {};
    for (const item of items.value) {
      const key = options.getKey(item);
      const posData = positions.get(key);
      if (posData) {
        itemPositions[key] = position + posData.transform;
        position += selectSize(posData);
      }
    }
    const comparator = (i1, i2) => itemPositions[options.getKey(i1)] - itemPositions[options.getKey(i2)];
    const itemsArray = items.value;
    if (!isSorted(itemsArray, comparator)) {
      e.preventDefault();
      e.stopPropagation();
      const newOrder = [...itemsArray];
      newOrder.sort(comparator);
      items.value = newOrder;
      options.onReorder(newOrder);
    }
    cancel();
  }
  function updateOffset(itemId, offset) {
    isReordering.value = true;
    const itemList = items.value;
    let i;
    for (i = 0; i < itemList.length && options.getKey(itemList[i]) !== itemId; i++) ;
    const itemPosition = positions.get(itemId);
    if (!itemPosition) {
      return;
    }
    itemPosition.transform = offset;
    if (offset > 0) {
      let currOffset = 0;
      for (i = i + 1; i < itemList.length; i++) {
        const next = positions.get(options.getKey(itemList[i]));
        if (!next) {
          continue;
        }
        if (offset > currOffset + selectSize(next) / 2) {
          next.transform = -selectSize(itemPosition);
        } else {
          next.transform = 0;
        }
        currOffset += selectSize(next);
      }
    } else if (offset < 0) {
      let currOffset = 0;
      for (i = i - 1; i >= 0; i--) {
        const next = positions.get(options.getKey(itemList[i]));
        if (!next) {
          continue;
        }
        if (offset < currOffset - selectSize(next) / 2) {
          next.transform = selectSize(itemPosition);
        } else {
          next.transform = 0;
        }
        currOffset -= selectSize(next);
      }
    }
  }
  function useItem(itemOptions) {
    positions.set(toValue(itemOptions.itemId), {
      width: 0,
      height: 0,
      transform: 0
    });
    const startPos = ref(0);
    const isDragging = ref(false);
    function clickEvent(e) {
      const wasTracking = isDragging.value;
      isDragging.value = false;
      if (wasTracking) {
        confirm(e);
      }
    }
    function upEvent(e) {
      setTimeout(() => clickEvent(e), 1);
    }
    function blurEvent() {
      const wasTracking = isDragging.value;
      isDragging.value = false;
      if (wasTracking) {
        cancel();
      }
    }
    function keyDownEvent(e) {
      if (e.key === "Escape") {
        const wasTracking = isDragging.value;
        isDragging.value = false;
        if (wasTracking) {
          cancel();
        }
      }
    }
    function updateSize() {
      const el = itemOptions.element.value;
      if (el) {
        const bounds = el.getBoundingClientRect();
        updateSizing(toValue(itemOptions.itemId), bounds.width, bounds.height);
      }
    }
    onMounted(() => {
      window.addEventListener("mouseup", upEvent);
      window.addEventListener("blur", blurEvent);
      window.addEventListener("keydown", keyDownEvent);
    });
    onUnmounted(() => {
      window.removeEventListener("mouseup", upEvent);
      window.removeEventListener("blur", blurEvent);
      window.removeEventListener("keydown", keyDownEvent);
    });
    onMounted(updateSize);
    onUpdated(updateSize);
    function mouseDown(e) {
      if (!isDragging.value && e.button === 0) {
        isDragging.value = true;
        startPos.value = selectMouseCoord();
      }
    }
    watch([mouseX, mouseY], () => {
      if (isDragging.value) {
        updateOffset(toValue(itemOptions.itemId), selectMouseCoord() - startPos.value);
      }
    });
    const offset = computed(() => positions.get(toValue(itemOptions.itemId))?.transform ?? 0);
    return {
      isDragging: computed(() => isDragging.value),
      offset,
      elementProps: computed(() => {
        const position = positions.get(toValue(itemOptions.itemId));
        const style = {
          userSelect: "none"
        };
        if (isReordering.value && position) {
          if (position.transform !== 0) {
            style.transform = `translate3d(${position.transform}px, 0, 0)`;
          }
          if (!isDragging.value) {
            style.transition = itemOptions.timingFunction ?? "transform 0.33s cubic-bezier(0.65, 0, 0.35, 1)";
          }
        }
        if (isDragging.value) {
          style.zIndex = options.defaultZIndex ?? 3;
          if (Math.abs(offset.value) > 2) {
            style.cursor = "move";
          }
        }
        return {
          onMousedown: mouseDown,
          onClick: clickEvent,
          style,
          onDragstart: (e) => e.preventDefault()
        };
      })
    };
  }
  return {
    useItem,
    isReordering: computed(() => isReordering.value)
  };
};
function useRouterParam(paramName, type, routerMode = "replace") {
  const router = useRouter();
  if (routerMode !== "push" && routerMode !== "replace") {
    throw new Error("Invalid routerMode");
  }
  function formatParam(value) {
    const stringified = type.stringify(value);
    if (typeof stringified !== "string") {
      throw new Error("Route param must serialize to a string");
    }
    return stringified;
  }
  return computed({
    get() {
      const route = router.currentRoute.value;
      return type.parse(route.params[paramName]);
    },
    set(newValue) {
      const route = router.currentRoute.value;
      const newRoute = {
        ...route,
        params: {
          ...route.params,
          [paramName]: formatParam(newValue)
        }
      };
      if (routerMode === "replace") {
        return router.replace(newRoute);
      } else if (routerMode === "push") {
        return router.push(newRoute);
      } else {
        throw new Error("Invalid router mode");
      }
    }
  });
}
const useTimeout = (delay2, action) => {
  const timeoutId = ref(null);
  onMounted(() => {
    timeoutId.value = window.setTimeout(action, delay2);
  });
  onUnmounted(() => {
    if (timeoutId.value !== null) {
      clearTimeout(timeoutId.value);
    }
  });
};
const useWindowListener = (type, listener, options) => {
  let registered = shallowRef(false);
  onMounted(() => {
    if (!registered.value) {
      window.addEventListener(type, listener, options);
      registered.value = true;
    }
  });
  onUnmounted(() => {
    if (registered.value) {
      unregister();
    }
  });
  function unregister() {
    window.removeEventListener(type, listener, options);
    registered.value = false;
  }
  return {
    isRegistered: registered,
    unregister
  };
};
const watchDebounce = (toWatch, action, timeout = 250) => {
  let timer = null;
  watch(toWatch, () => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => action(), timeout);
  });
};
function isBodyObject(value) {
  return value instanceof ArrayBuffer || ArrayBuffer.isView(value) || value instanceof Blob || value instanceof File || value instanceof URLSearchParams || value instanceof FormData || value instanceof ReadableStream;
}
class AbstractHttpResponse {
  constructor(service, requestOptions = {}) {
    this.service = service;
    this.requestOptions = requestOptions;
  }
  mergeOptions(options) {
    return {
      ...this.requestOptions,
      ...options,
      headers: {
        ...this.requestOptions?.headers,
        ...options?.headers
      }
    };
  }
  withRequestData(body) {
    if (body === void 0) {
      return this;
    }
    if (isBodyObject(body)) {
      return this.with({ body });
    }
    return this.with({
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json"
      }
    });
  }
  async request(url, method = this.requestOptions.method) {
    return this.service.fetch({
      ...this.requestOptions,
      url,
      method
    });
  }
  async get(url) {
    const response = await this.request(url, "GET");
    return await this.mapResult(response);
  }
  async post(url, requestData) {
    const response = await this.withRequestData(requestData).request(url, "POST");
    return await this.mapResult(response);
  }
  async put(url, requestData) {
    const response = await this.withRequestData(requestData).request(url, "PUT");
    return await this.mapResult(response);
  }
  async patch(url, requestData) {
    const response = await this.withRequestData(requestData).request(url, "PATCH");
    return await this.mapResult(response);
  }
  async delete(url) {
    const response = await this.request(url, "DELETE");
    return await this.mapResult(response);
  }
}
class BlobResponse extends AbstractHttpResponse {
  with(options) {
    return new BlobResponse(this.service, this.mergeOptions(options));
  }
  async mapResult(response) {
    return await response.blob();
  }
}
class JsonResponse extends AbstractHttpResponse {
  with(options) {
    return new JsonResponse(this.service, this.mergeOptions(options));
  }
  async mapResult(response) {
    const json = await response.json();
    return json;
  }
}
class RawResponse extends AbstractHttpResponse {
  with(options) {
    return new RawResponse(this.service, this.mergeOptions(options));
  }
  mapResult(response) {
    return Promise.resolve(response);
  }
}
class SchemaException extends TypeError {
  constructor(rawMessage, path = []) {
    super(rawMessage + " @ " + SchemaException.stringifyPath(path));
    this.rawMessage = rawMessage;
    this.path = path;
  }
  withAddedPath(path) {
    return new SchemaException(this.rawMessage, [path, ...this.path]);
  }
  static stringifyPath(path) {
    let result = "";
    for (let i = 0; i < path.length; i++) {
      const entry = path[i];
      if (typeof entry === "number") {
        result += `[${entry}]`;
      } else {
        if (i != 0) {
          result += ".";
        }
        result += entry;
      }
    }
    return result;
  }
}
class AbstractApiSchema {
}
class PrimitiveSchema extends AbstractApiSchema {
  constructor(inputType, mappingFn) {
    super();
    this.inputType = inputType;
    this.mappingFn = mappingFn;
  }
  convert(value) {
    if (typeof value !== this.inputType) {
      throw new SchemaException(`Expected ${this.inputType}, got ${typeof value}`);
    }
    return this.mappingFn(value);
  }
}
function primitiveSchema(inputType, mappingFn) {
  return new PrimitiveSchema(inputType, mappingFn);
}
const StringSchema = primitiveSchema("string", (s) => s);
const NumberSchema = primitiveSchema("number", (value) => {
  const result = Number(value);
  if (!Number.isFinite(result)) {
    throw new TypeError(`Invalid number '${String(value)}'`);
  }
  return result;
});
const BooleanSchema = primitiveSchema("boolean", (value) => value);
const replacementSchemas = /* @__PURE__ */ new Map([
  [String, StringSchema],
  [Boolean, BooleanSchema],
  [Number, NumberSchema]
]);
function mapApiType(schema, value) {
  if (schema instanceof AbstractApiSchema) {
    return schema.convert(value);
  }
  const replacement = replacementSchemas.get(schema);
  if (replacement) {
    return replacement.convert(value);
  }
  return schema(value);
}
function schemaClone(schema, value) {
  const jsonData = JSON.stringify(value);
  const apiData = JSON.parse(jsonData);
  return mapApiType(schema, apiData);
}
class ArraySchema extends AbstractApiSchema {
  /**
   * @param schema The schema of the type of the array contents. E.g. number[] would use the schema type of Number
   */
  constructor(schema) {
    super();
    this.schema = schema;
    this.schema = schema;
  }
  convert(value) {
    if (value === null || value === void 0) {
      throw new SchemaException(`Invalid array ${String(value)}`);
    }
    const result = new Array(value.length);
    for (let i = 0; i < value.length; i++) {
      try {
        result[i] = mapApiType(this.schema, value[i]);
      } catch (e) {
        if (e instanceof SchemaException) {
          throw e.withAddedPath(i);
        }
        throw new SchemaException(e.message, [i]);
      }
    }
    return result;
  }
}
AbstractApiSchema.prototype.array = function() {
  return new ArraySchema(this);
};
function arraySchema(schema) {
  return new ArraySchema(schema);
}
class ConstructorSchema extends AbstractApiSchema {
  constructor(constructorValue) {
    super();
    this.constructorValue = constructorValue;
  }
  convert(value) {
    return new this.constructorValue(value);
  }
}
function constructorSchema(constructor) {
  return new ConstructorSchema(constructor);
}
class FunctionSchema extends AbstractApiSchema {
  constructor(mapping) {
    super();
    this.mapping = mapping;
  }
  convert(value) {
    return this.mapping(value);
  }
}
function functionSchema(mapping) {
  return new FunctionSchema(mapping);
}
const DateSchema = functionSchema((dateData) => {
  const date = new Date(dateData);
  if (!Number.isFinite(date.valueOf())) {
    throw new TypeError(`Invalid date '${String(dateData)}'`);
  }
  return date;
});
class EnumSchema extends AbstractApiSchema {
  constructor(enumValues) {
    super();
    this.enumValues = enumValues;
    this.possibleValues = new Set(Object.values(enumValues));
  }
  possibleValues;
  convert(value) {
    if (!this.possibleValues.has(value)) {
      throw new SchemaException(`Invalid enum value '${value}'`);
    }
    return value;
  }
}
function enumSchema(enumValues) {
  return new EnumSchema(enumValues);
}
class NullableSchema extends AbstractApiSchema {
  constructor(schema) {
    super();
    this.schema = schema;
  }
  convert(value) {
    if (value === null) {
      return null;
    }
    return mapApiType(this.schema, value);
  }
}
AbstractApiSchema.prototype.nullable = function() {
  return new NullableSchema(this);
};
function nullableSchema(schema) {
  return new NullableSchema(schema);
}
class ObjectSchema extends AbstractApiSchema {
  constructor(properties, copyUnknownProperties = false) {
    super();
    this.properties = properties;
    this.copyUnknownProperties = copyUnknownProperties;
  }
  convert(value) {
    if (value === null || value === void 0) {
      throw new TypeError(`Invalid object ${String(value)}`);
    }
    const result = {};
    if (this.copyUnknownProperties) {
      Object.assign(result, value);
    }
    for (const propertyName in this.properties) {
      const propertySchema = this.properties[propertyName];
      const existingValue = value[propertyName];
      try {
        result[propertyName] = mapApiType(propertySchema, existingValue);
      } catch (e) {
        if (e instanceof SchemaException) {
          throw e.withAddedPath(propertyName);
        }
        throw new SchemaException(e.message, [propertyName]);
      }
    }
    return result;
  }
}
function objectSchema(properties, copyUnknownProperties = false) {
  return new ObjectSchema(properties, copyUnknownProperties);
}
function schemaProperties(...properties) {
  const result = {};
  for (const property of properties) {
    if (property instanceof ObjectSchema) {
      Object.assign(result, property.properties);
    } else {
      Object.assign(result, property);
    }
  }
  return result;
}
class OptionalSchema extends AbstractApiSchema {
  constructor(schema) {
    super();
    this.schema = schema;
  }
  convert(value) {
    if (value === void 0) {
      return void 0;
    }
    return mapApiType(this.schema, value);
  }
}
AbstractApiSchema.prototype.optional = function() {
  return new OptionalSchema(this);
};
function optionalSchema(schema) {
  return new OptionalSchema(schema);
}
class RecordSchema extends AbstractApiSchema {
  constructor(keySchema, valueSchema) {
    super();
    this.keySchema = keySchema;
    this.valueSchema = valueSchema;
  }
  convert(value) {
    const result = {};
    for (let key in value) {
      try {
        const newKey = mapApiType(this.keySchema, key);
        result[newKey] = mapApiType(this.valueSchema, value[key]);
      } catch (e) {
        if (e instanceof SchemaException) {
          throw e.withAddedPath(key);
        }
        throw new SchemaException(e.message, [key]);
      }
    }
    return result;
  }
}
function recordSchema(keySchema, valueSchema) {
  return new RecordSchema(keySchema, valueSchema);
}
class SchemaJsonResponse extends AbstractHttpResponse {
  constructor(service, schema, requestOptions = {}) {
    super(service, requestOptions);
    this.schema = schema;
  }
  with(options) {
    return new SchemaJsonResponse(this.service, this.schema, this.mergeOptions(options));
  }
  async mapResult(response) {
    const json = await response.json();
    return mapApiType(this.schema, json);
  }
}
class TextResponse extends AbstractHttpResponse {
  with(options) {
    return new TextResponse(this.service, this.mergeOptions(options));
  }
  async mapResult(response) {
    return await response.text();
  }
}
class VoidResponse extends AbstractHttpResponse {
  with(options) {
    return new VoidResponse(this.service, this.mergeOptions(options));
  }
  mapResult() {
    return Promise.resolve();
  }
}
class AbstractHttpService {
  returning(schema) {
    return new SchemaJsonResponse(this, schema);
  }
  returningBlob() {
    return new BlobResponse(this);
  }
  returningJson() {
    return new JsonResponse(this);
  }
  returningText() {
    return new TextResponse(this);
  }
  returningVoid() {
    return new VoidResponse(this);
  }
  returningResponse() {
    return new RawResponse(this);
  }
}
const piniaLoadingState = () => ({
  type: "initial",
  error: void 0,
  result: void 0
});
const piniaLoadableState = (queryData) => ({
  ...piniaLoadingState(),
  queryData
});
const mergeLoading = (entries) => {
  const result = {};
  for (const key in entries) {
    const entry = entries[key];
    switch (entry.type) {
      case "initial":
      case "loading":
      case "error":
        return entry;
      default:
        result[key] = entry.result;
    }
  }
  return {
    type: "done",
    result
  };
};
const loadLoadable = async (loading) => {
  assignLoadable(loading, { type: "loading" });
  try {
    const data = await loading.queryData();
    assignLoadable(loading, {
      type: "done",
      result: data
    });
  } catch (e) {
    console.error(e);
    assignLoadable(loading, {
      type: "error",
      error: e
    });
  }
  return loading;
};
const assignLoadable = (obj, state) => {
  const objAs = obj;
  if ("error" in objAs) {
    objAs.error = void 0;
  }
  if ("result" in objAs) {
    objAs.result = void 0;
  }
  Object.assign(obj, state);
};
const ensureLoadingHasResult = (loading) => {
  switch (loading.type) {
    case "loading":
      throw new Error("Data still loading");
    case "initial":
      throw new Error("Data not yet requested");
    case "error":
      throw loading.error;
  }
  return loading.result;
};
const useLoadableRef = (queryData) => {
  return shallowReactive(piniaLoadableState(queryData));
};
const toLoadable = (loading, queryData) => {
  return reactive({
    type: toRef(loading, "type"),
    error: toRef(loading, "error"),
    result: toRef(loading, "result"),
    queryData
  });
};
class LoadingError extends Error {
  constructor(title, message) {
    super(message);
    this.title = title;
  }
}
const createLoadingHook = (options) => {
  const {
    loadingComponent: LoadingComponent,
    showError: showErrorMessage,
    coolInTimeMs,
    defaultError = "An unexpected error occurred. Try again or contact an administrator",
    defaultErrorTitle = "Unexpected Error"
  } = options;
  const runningActions = ref(0);
  const computedActions = computed(() => runningActions.value);
  const loading = computed(() => runningActions.value > 0);
  async function showError(e) {
    if (e instanceof LoadingError) {
      await showErrorMessage(e.title, e.message);
    } else {
      const title = typeof defaultErrorTitle === "function" ? defaultErrorTitle(e) : defaultErrorTitle;
      const message = typeof defaultError === "function" ? defaultError(e) : defaultError;
      console.error(e);
      await showErrorMessage(title, message);
    }
  }
  const runAction = async (action) => {
    try {
      runningActions.value++;
      const t1 = Date.now();
      const result = await action;
      const t2 = Date.now();
      const dt = t2 - t1;
      if (coolInTimeMs !== void 0 && runningActions.value === 1 && dt >= coolInTimeMs && dt <= coolInTimeMs + 250) {
        await delay(coolInTimeMs + 250 - dt);
      }
      runningActions.value--;
      return result;
    } catch (e) {
      runningActions.value--;
      await showError(e);
      return Promise.reject(e);
    }
  };
  const wrapAction = (action) => (...args) => runAction(action(...args));
  const LoadingWrapper = /* @__PURE__ */ defineComponent({
    name: "LoadingWrapper",
    setup: (_, {
      slots
    }) => {
      return () => createVNode(Fragment, null, [renderSlot$1(slots, "default"), createVNode("div", {
        "key": "loading-component",
        "style": {
          display: "contents"
        }
      }, [loading.value ? createVNode(LoadingComponent, null, null) : null])]);
    }
  });
  return {
    runningActions: computedActions,
    loading,
    LoadingWrapper,
    showError,
    useLoading: () => ({
      loading,
      runningActions: computedActions,
      runAction,
      wrapAction
    })
  };
};
const createStateLoading = (options) => {
  const StateLoading = (props, { slots }) => {
    const loadingStatus = computed(() => {
      if (props.stores.every((store) => store.type === "done")) {
        return { type: "done", result: null };
      }
      if (props.stores.some((store) => store.type === "loading")) {
        return { type: "loading" };
      }
      const errorStore = props.stores.find((store) => store.type === "error");
      if (errorStore && errorStore.type === "error") {
        return { type: "error", error: errorStore.error };
      }
      return { type: "initial" };
    });
    const loadFailed = async () => {
      const stores = props.stores.filter((loadable) => loadable.type === "error").map(loadLoadable);
      await Promise.all(stores);
    };
    const reloadProps = async () => {
      const stores = props.stores.filter((loadable) => !loadable.type || loadable.type === "initial").map(loadLoadable);
      await Promise.all(stores);
    };
    onBeforeMount(() => void reloadProps());
    watch(toRef(props, "stores"), () => void reloadProps());
    return () => {
      switch (loadingStatus.value.type) {
        case "loading":
          return h(props.LoadingComponent ?? options.LoadingComponent);
        case "error":
          return h(props.ErrorComponent ?? options.ErrorComponent, { error: loadingStatus.value.error, reload: loadFailed });
        case "done":
          return renderSlot$1(slots, "default");
      }
      return null;
    };
  };
  return defineComponent(StateLoading, {
    stores: requiredProp(Array),
    ErrorComponent: optionalProp([Function, Object]),
    LoadingComponent: optionalProp([Function, Object])
  });
};
var ConfirmResult = /* @__PURE__ */ ((ConfirmResult2) => {
  ConfirmResult2["Confirm"] = "confirm";
  ConfirmResult2["Cancel"] = "cancel";
  ConfirmResult2["Closed"] = "closed";
  return ConfirmResult2;
})(ConfirmResult || {});
function createPopups(popupOptions) {
  const popups = shallowRef([]);
  function openPopup(popup) {
    popups.value = [...popups.value, popup];
  }
  function closeTopPopup() {
    if (popups.value.length === 0) {
      return null;
    }
    const newPopups = [...popups.value];
    const poppedPopup = newPopups.pop();
    popups.value = newPopups;
    return poppedPopup ?? null;
  }
  function closePopup(popup) {
    const index = popups.value.findIndex((p) => p === popup);
    if (index >= 0) {
      const newPopups = [...popups.value];
      newPopups.splice(index, 1);
      popups.value = newPopups;
    }
    return index;
  }
  function showConfirmPopup(options) {
    if (typeof options === "function") {
      options = {
        content: options
      };
    }
    const {
      confirmText = "Ok",
      cancelText = "Cancel",
      content: Content
    } = options;
    const ConfirmPopup = popupOptions.ConfirmPopup;
    return new Promise((resolve) => openPopup({
      ...options,
      onClose: () => resolve(ConfirmResult.Closed),
      content: ({
        close
      }) => createVNode(ConfirmPopup, {
        "confirmText": confirmText,
        "cancelText": cancelText,
        "onCancel": () => {
          close();
          resolve(ConfirmResult.Cancel);
        },
        "onSubmit": () => {
          close();
          resolve(ConfirmResult.Confirm);
        }
      }, {
        default: () => [createVNode(Content, null, null)]
      })
    }));
  }
  async function confirm(options) {
    const result = await showConfirmPopup(options);
    return result === ConfirmResult.Confirm;
  }
  function showAlertPopup(options) {
    if (typeof options === "function") {
      options = {
        content: options,
        ...options
      };
    }
    const {
      okText = "Ok",
      content: Content
    } = options;
    const AlertPopup = popupOptions.AlertPopup;
    return new Promise((resolve) => openPopup({
      ...options,
      onClose: resolve,
      content: ({
        close
      }) => createVNode(AlertPopup, {
        "okText": okText,
        "onClose": () => {
          close();
          resolve();
        }
      }, {
        default: () => [createVNode(Content, null, null)]
      })
    }));
  }
  return {
    popups,
    openPopup,
    closeTopPopup,
    closePopup,
    showConfirmPopup,
    confirm,
    showAlertPopup
  };
}
export {
  AbstractApiSchema,
  AbstractHttpResponse,
  AbstractHttpService,
  Alert,
  ArraySchema,
  AutoColumns,
  BlobResponse,
  BooleanSchema,
  ColumnLayout,
  ConfirmResult,
  ConstructorSchema,
  DateSchema,
  Deferred,
  EnumSchema,
  FormTabControls,
  FormTabs,
  FormTabsRenderingMethod,
  FunctionSchema,
  JsonResponse,
  Link,
  LoadingError,
  Modal,
  ModalTitle,
  NullableSchema,
  NumberSchema,
  ObjectSchema,
  OptionalSchema,
  PrimitiveSchema,
  RawResponse,
  RecordSchema,
  RefQueries,
  Required,
  SchemaException,
  SchemaJsonResponse,
  ScrollPane,
  ShadowContainer,
  StringSchema,
  TextResponse,
  ToggleButton,
  ValidationHandler,
  ValidationHandlerKey,
  VoidResponse,
  anyProp,
  arraySchema,
  assignLoadable,
  calculateForegroundColor,
  castUnsafe,
  clamp,
  componentProp,
  constructorSchema,
  createLoadingHook,
  createPopups,
  createStateLoading,
  debounce,
  defineComponent,
  defineGlobal,
  defineGlobals,
  delay,
  downloadFile,
  ensureLoadingHasResult,
  enumSchema,
  findFileNameFromHeader,
  findFileNameFromHeaders,
  formatFileSize,
  functionSchema,
  getPageCount,
  getUnique,
  groupBy,
  hsvToRGB,
  inferProps,
  isBodyObject,
  isSorted,
  lazy,
  loadLoadable,
  mapApiType,
  mapDefined,
  mapValues,
  mergeLoading,
  mergePromises,
  nullableSchema,
  objectSchema,
  optionalProp,
  optionalSchema,
  paged,
  parsingNullableString,
  parsingString,
  piniaLoadableState,
  piniaLoadingState,
  positiveMod,
  primitiveSchema,
  propsWithDefaults,
  proxyProp,
  recordSchema,
  refSetter,
  renderSlot,
  requiredProp,
  runWithMinimumDelay,
  sanitiseQueryParams,
  schemaClone,
  schemaProperties,
  serialiseQueryValue,
  sortWithDirection,
  stylesheet,
  toLoadable,
  urlWithParams,
  useComputedQueryRef,
  useCustomCheck,
  useCustomFormValidation,
  useCustomInputValidation,
  useCustomValidation,
  useInputValidation,
  useInterval,
  useLoadableRef,
  useMouse,
  useOnInput,
  useOnInputDate,
  useOnInputNumber,
  useOptionalRouterParam,
  usePollRef,
  useQueryParam,
  useQueryRef,
  useQueryStoreRef,
  useReordering,
  useRouterParam,
  useService,
  useTimeout,
  useValidationHandler,
  useWindowListener,
  waitForDocumentReady,
  watchDebounce
};
//# sourceMappingURL=index.js.map
